/**
 * PlaudImportModal — Import recordings from Plaud MCP.
 *
 * Flow:
 * 1. Lists available recordings from user's Plaud account
 * 2. User selects recordings to import
 * 3. Syncs to backend with optional account linking
 *
 * MCP Tools used: mcp__plaud__list_files, mcp__plaud__get_file,
 *                 mcp__plaud__get_note, mcp__plaud__get_transcript
 */

import React, { useState, useEffect } from 'react';
import { X, Mic, Clock, Calendar, Check, AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import apiClient from '../../lib/apiClient';

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDuration(ms) {
  if (!ms) return '0s';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h${minutes.toString().padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m${seconds.toString().padStart(2, '0')}s`;
  return `${seconds}s`;
}

function PlaudImportModal({ accountId, accountName, onClose, onSuccess }) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  // Fetch available recordings from Plaud
  const fetchRecordings = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/plaud/available');
      // Filter out already-synced recordings by default
      const available = (response.files || []).filter(f => !f.already_synced);
      setRecordings(available);
    } catch (err) {
      // Handle specific error codes from backend
      // apiClient attaches parsed JSON error to err.data
      const data = err.data || {};
      if (data.code === 'NOT_CONNECTED') {
        setError('Connect your Plaud account in Settings to import recordings.');
      } else if (data.code === 'TOKEN_EXPIRED' || data.code === 'AUTH_FAILED') {
        setError('Your Plaud session has expired. Please reconnect in Settings.');
      } else if (data.code === 'SERVICE_UNAVAILABLE') {
        setError('Plaud service is temporarily unavailable. Please try again later.');
      } else if (err.status === 404 || err.message?.includes('404')) {
        setError('Plaud integration is not configured. Please contact support.');
      } else {
        setError(data.message || err.message || 'Failed to fetch recordings');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecordings();
  }, []);

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelected(next);
  };

  const selectAll = () => {
    if (selected.size === recordings.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(recordings.map(r => r.id)));
    }
  };

  const handleImport = async () => {
    if (selected.size === 0) return;

    setImporting(true);
    setImportProgress({ current: 0, total: selected.size });

    try {
      const toImport = recordings.filter(r => selected.has(r.id));
      const items = toImport.map(r => ({
        plaud_file_id: r.id,
        file: {
          id: r.id,
          name: r.name,
          created_at: r.created_at || r.recorded_at,
          duration_ms: r.duration,
        },
        account_id: accountId,
      }));

      // Sync to backend
      const result = await apiClient.post('/api/plaud/sync', items);

      if (result.error_count > 0) {
        console.warn('Some recordings failed to import:', result.errors);
      }

      onSuccess?.();
    } catch (err) {
      setError(err.message || 'Import failed');
      setImporting(false);
    }
  };

  return (
    <div className="aw-modal-overlay" onClick={onClose}>
      <div className="aw-modal aw-plaud-import-modal" onClick={e => e.stopPropagation()}>
        <header className="aw-modal-header">
          <h2><Mic size={18} /> Import from Plaud</h2>
          <button className="aw-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </header>

        <div className="aw-modal-body">
          {accountName && (
            <p className="aw-plaud-import-context">
              Importing to: <strong>{accountName}</strong>
            </p>
          )}

          {loading && (
            <div className="aw-plaud-import-loading">
              <Loader2 size={24} className="aw-spin" />
              <p>Fetching recordings from Plaud...</p>
            </div>
          )}

          {error && (
            <div className="aw-plaud-import-error">
              <AlertCircle size={18} />
              <p>{error}</p>
              <button onClick={fetchRecordings}>
                <RefreshCw size={14} /> Retry
              </button>
            </div>
          )}

          {!loading && !error && recordings.length === 0 && (
            <div className="aw-plaud-import-empty">
              <Mic size={32} strokeWidth={1} />
              <p>No recordings found in your Plaud account.</p>
              <p className="aw-plaud-import-hint">
                Record meetings with your Plaud device, then import them here.
              </p>
            </div>
          )}

          {!loading && !error && recordings.length > 0 && (
            <>
              <div className="aw-plaud-import-toolbar">
                <button
                  className="aw-plaud-select-all"
                  onClick={selectAll}
                >
                  <Check size={14} />
                  {selected.size === recordings.length ? 'Deselect All' : 'Select All'}
                </button>
                <span className="aw-plaud-import-count">
                  {selected.size} of {recordings.length} selected
                </span>
              </div>

              <div className="aw-plaud-import-list">
                {recordings.map(rec => (
                  <div
                    key={rec.id}
                    className={`aw-plaud-import-item ${selected.has(rec.id) ? 'aw-plaud-item-selected' : ''}`}
                    onClick={() => toggleSelect(rec.id)}
                  >
                    <div className="aw-plaud-item-check">
                      {selected.has(rec.id) && <Check size={14} />}
                    </div>
                    <div className="aw-plaud-item-info">
                      <span className="aw-plaud-item-name">{rec.name}</span>
                      <span className="aw-plaud-item-meta">
                        <span><Calendar size={11} /> {fmtDate(rec.created_at || rec.recorded_at)}</span>
                        <span><Clock size={11} /> {fmtDuration(rec.duration)}</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <footer className="aw-modal-footer">
          <button className="aw-btn aw-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="aw-btn aw-btn-primary"
            onClick={handleImport}
            disabled={selected.size === 0 || importing}
          >
            {importing ? (
              <>
                <Loader2 size={14} className="aw-spin" />
                Importing {importProgress.current}/{importProgress.total}
              </>
            ) : (
              <>Import {selected.size} Recording{selected.size !== 1 ? 's' : ''}</>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default PlaudImportModal;
